import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { pocApi, frameImageURL, LABEL_STYLE, CONDITION_LABELS, SKIP_REASON_LABELS, MECHANISM_LABELS } from '../utils/pocApi';

// PH0: two people draw the same frames independently, and the disagreement is
// the finding.
//
// Independence is enforced here rather than asked for. The other annotator's
// boxes cannot be displayed on a frame the current annotator has not submitted
// yet — being able to peek would make the agreement number measure copying
// rather than whether the label definition holds.

// 描く順に並べる。まず景品ブロック（ガラス空間1つ ＋ 爪1つ ＋ 操作パネル1つ）を
// 1つずつ、次にそれを 1/2/4 個まとめてキャビネット。
//
// ブロックを主クラスにしたのは、キャビネットの外周が人によって揺れるため。
// 人が描いた枠を数えたところ、1台の中の操作パネル数が 1/2/3/4/5 に割れ、
// 3個・5個は隣の台まで囲った枠だった。ブロックは爪と操作パネルが1対1で
// 対応するので、二人が描いても同じ枠になる。
const LABELS = ['block', 'cabinet', 'panel'];
const MIN_SIZE = 0.005; // smaller than this is a stray click, not a box

// The grab area of a box: a strip along each edge, so the middle stays free for
// drawing the next box inside it.
const EDGE = 9;
const EDGES = [
  { key: 'n', style: { left: -EDGE / 2, right: -EDGE / 2, top: -EDGE / 2, height: EDGE } },
  { key: 's', style: { left: -EDGE / 2, right: -EDGE / 2, bottom: -EDGE / 2, height: EDGE } },
  { key: 'w', style: { top: -EDGE / 2, bottom: -EDGE / 2, left: -EDGE / 2, width: EDGE } },
  { key: 'e', style: { top: -EDGE / 2, bottom: -EDGE / 2, right: -EDGE / 2, width: EDGE } },
];

let nextLocalId = 1;

export default function PocAnnotate() {
  useAuthGuard();
  const { datasetId } = useParams();
  // 作業する枚数を人に渡すときは、フレーム番号を口で伝えるのではなくURLで渡す。
  // ?seq=8&a=C で「seq 8 を C として開く」。15枚の作業リストがそのまま
  // リンクの列になり、開く場所を間違えようがなくなる。
  const [search, setSearch] = useSearchParams();

  const [dataset, setDataset] = useState(null);
  const [frames, setFrames] = useState([]);
  const [playPositions, setPlayPositions] = useState([]);
  const [index, setIndex] = useState(0);

  const [annotator, setAnnotator] = useState(
    () => search.get('a') || localStorage.getItem('poc-annotator') || 'A');
  const [boxes, setBoxes] = useState([]);
  const [otherBoxes, setOtherBoxes] = useState([]);
  // 検出器の枠。PH1.5で「この台は動画のどこかで見つかったか」を人が答えるとき、
  // 見つかったかどうかが画面で分からないと答えようがない。
  const [detectorBoxes, setDetectorBoxes] = useState([]);
  const [showDetector, setShowDetector] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeLabel, setActiveLabel] = useState('block');
  const [showOther, setShowOther] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // The frame this annotator ruled out, and why. Held apart from `submitted`
  // because "I looked and there is nothing here" and "I looked and I cannot
  // judge this" are different answers, and only the first one is ground truth.
  const [skipReason, setSkipReason] = useState('');
  // The server owns the list; a hardcoded one here would drift and start
  // sending reasons the database rejects.
  const [skipReasons, setSkipReasons] = useState(Object.keys(SKIP_REASON_LABELS));
  // 語彙はサーバが持つ。画面に焼くと、定義を変えたときに片方だけが古くなる。
  const [vocabulary, setVocabulary] = useState({ mechanisms: Object.keys(MECHANISM_LABELS) });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  const surfaceRef = useRef(null);
  const dragRef = useRef(null);

  const frame = frames[index];
  // The identity of `frames[index]` changes whenever anything about the frame
  // is written back to the list. The id does not, and the id is what the
  // reload actually depends on.
  const frameId = frame ? frame.id : null;

  useEffect(() => { localStorage.setItem('poc-annotator', annotator); }, [annotator]);

  const loadDataset = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await pocApi.getDataset(datasetId);
      setDataset(data.dataset);
      setFrames(data.frames || []);
      setPlayPositions(data.playPositions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => { loadDataset(); }, [loadDataset]);

  // ?seq= が指す枚を最初に一度だけ開く。以降は人の操作が主なので、URLに
  // 引き戻されないよう ref で1回に限定する。
  const seqApplied = useRef(false);
  useEffect(() => {
    if (seqApplied.current || !frames.length) return;
    const want = Number(search.get('seq'));
    if (want) {
      const at = frames.findIndex((f) => f.seq === want);
      if (at >= 0) setIndex(at);
    }
    seqApplied.current = true;
  }, [frames, search]);

  // 逆向きにも同期する。今見ている枚がURLに出ていれば、そのまま人に渡せる
  useEffect(() => {
    const f = frames[index];
    if (!f || String(f.seq) === search.get('seq')) return;
    const next = new URLSearchParams(search);
    next.set('seq', String(f.seq));
    setSearch(next, { replace: true });
  }, [frames, index, search, setSearch]);

  useEffect(() => {
    let cancelled = false;
    pocApi.vocabulary()
      .then((v) => {
        if (cancelled) return;
        if (v.skipReasons) setSkipReasons(v.skipReasons);
        setVocabulary(v || {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Re-read the frame when the frame or the annotator changes: switching from
  // A to B must show B's work, not A's left on screen.
  //
  // Keyed on the id, not the frame object. Saving a condition replaces that
  // frame's entry in `frames`, and depending on the object meant every
  // condition toggle re-ran this and overwrote the boxes on screen with the
  // server's copy — silently throwing away anything not yet submitted.
  //
  // Submitted / skipped come from the frame's own review rows for the same
  // reason: reading them off the list would put the list's identity back into
  // the dependencies.
  useEffect(() => {
    if (!frameId) return;
    let cancelled = false;

    (async () => {
      try {
        const data = await pocApi.getFrame(frameId);
        if (cancelled) return;

        const all = data.boxes || [];
        setBoxes(
          all
            .filter((b) => b.source === 'human' && b.author === annotator)
            .map((b) => ({ ...b, localId: nextLocalId++ }))
        );
        setOtherBoxes(all.filter((b) => b.source === 'human' && b.author !== annotator));
        setDetectorBoxes(all.filter((b) => b.source === 'detector'));
        setConditions(data.frame?.conditions || []);
        const mine = (data.reviews || []).find((r) => r.author === annotator);
        setSubmitted(!!mine && mine.status === 'done');
        setSkipReason(mine && mine.status === 'skipped' ? (mine.skipReason || 'other') : '');
        setSelectedId(null);
        setDirty(false);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [frameId, annotator]);

  const updateBox = (localId, patch) => {
    setBoxes((prev) => prev.map((b) => (b.localId === localId ? { ...b, ...patch } : b)));
    setDirty(true);
  };

  const removeBox = useCallback((localId) => {
    setBoxes((prev) => prev.filter((b) => b.localId !== localId));
    setSelectedId(null);
    setDirty(true);
  }, []);

  const save = useCallback(async (status = 'done', reason = '') => {
    if (!frame) return;
    // A cabinet whose panel is out of shot is not an unusable frame: boxing the
    // cabinet and drawing no panel is exactly the measurement that gives PH1.5
    // the ceiling on panel-based coverage. Excluding it would delete the
    // finding along with the frame.
    // 画面の端に接している枠は、実体がその先に続いている。見切れの印が無いと
    // 「半分しか観測していない台」が完全な1台として採点の分母に入り、
    // 的中率が実態より高く出る。人が気づける類の見落としではないので止める。
    const EDGE_EPS = 0.004;
    const atEdge = (b) => b.x <= EDGE_EPS || b.y <= EDGE_EPS
      || b.x + b.w >= 1 - EDGE_EPS || b.y + b.h >= 1 - EDGE_EPS;
    if (status === 'done') {
      const missed = boxes.filter((b) => atEdge(b) && !b.truncated);
      if (missed.length) {
        setError(`画面の端に接している枠が ${missed.length} 個あります。`
          + '実体が画面の外に続いているなら「見切れ」にチェックを入れてください'
          + '（本当に端で終わっているなら、枠を1pxだけ内側に寄せてください）。');
        setSelectedId(missed[0].localId);
        return;
      }
    }

    if (status === 'skipped' && reason === 'no_panel'
        && boxes.some((b) => b.label === 'cabinet' || b.label === 'block')) {
      setError('景品ブロック / Cabinet の枠が描かれています。台が囲えるなら除外せず、Control Panel を描かないまま「完了にして次へ」を押してください（それがパネル方式の上限の測定になります）。');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await pocApi.saveBoxes(frame.id, {
        source: 'human',
        author: annotator,
        status,
        skipReason: status === 'skipped' ? reason : '',
        // A skipped frame is saved with no boxes: the server drops them
        // anyway, and sending them would suggest the frame carries an opinion.
        boxes: (status === 'skipped' ? [] : boxes).map((b) => ({
          label: b.label,
          x: b.x, y: b.y, w: b.w, h: b.h,
          occluded: !!b.occluded,
          truncated: !!b.truncated,
          mechanismType: b.label === 'block' ? (b.mechanismType || '') : '',
          clawCount: b.clawCount === '' || b.clawCount == null ? null : Number(b.clawCount),
          playPositionId: b.playPositionId || '',
        })),
      });
      setDirty(false);
      setSubmitted(status === 'done');
      if (status === 'skipped') {
        setSkipReason(reason);
        setBoxes([]);
        setSelectedId(null);
      } else {
        setSkipReason('');
      }

      // Keep the filmstrip's done markers honest without refetching the whole
      // dataset on every save.
      setFrames((prev) =>
        prev.map((f) => {
          if (f.id !== frame.id) return f;
          const doneBy = new Set(f.doneBy || []);
          const skippedBy = new Set(f.skippedBy || []);
          if (status === 'done') doneBy.add(annotator); else doneBy.delete(annotator);
          if (status === 'skipped') skippedBy.add(annotator); else skippedBy.delete(annotator);
          return {
            ...f,
            doneBy: Array.from(doneBy),
            skippedBy: Array.from(skippedBy),
            humanBoxes: status === 'skipped' ? 0 : boxes.length,
          };
        })
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [frame, annotator, boxes]);

  const go = useCallback((delta) => {
    setIndex((i) => Math.min(frames.length - 1, Math.max(0, i + delta)));
  }, [frames.length]);

  // Save and move on in one key, because that is the actual loop: draw, submit,
  // next. Made explicit rather than auto-saving on navigation, so a half-drawn
  // frame is never recorded as a finished one.
  const saveAndNext = useCallback(async () => {
    await save('done');
    go(1);
  }, [save, go]);

  const toggleCondition = async (key) => {
    const next = conditions.includes(key)
      ? conditions.filter((c) => c !== key)
      : [...conditions, key];
    setConditions(next);
    try {
      await pocApi.saveConditions(frame.id, next);
      setFrames((prev) => prev.map((f) => (f.id === frame.id ? { ...f, conditions: next } : f)));
    } catch (e) {
      setError(e.message);
    }
  };

  // --- drawing ---

  const pointToNorm = (e) => {
    const rect = surfaceRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const onSurfaceMouseDown = (e) => {
    if (e.button !== 0 || !frame) return;
    const start = pointToNorm(e);
    dragRef.current = { mode: 'draw', start };
    const localId = nextLocalId++;
    setBoxes((prev) => [...prev,
      {
        localId, label: activeLabel, x: start.x, y: start.y, w: 0, h: 0,
        occluded: false, truncated: false, mechanismType: '', clawCount: '',
      }]);
    setSelectedId(localId);
    dragRef.current.localId = localId;
    e.preventDefault();
  };

  const onBoxMouseDown = (e, box, handle) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    setSelectedId(box.localId);
    dragRef.current = {
      mode: handle ? 'resize' : 'move',
      handle,
      localId: box.localId,
      start: pointToNorm(e),
      origin: { x: box.x, y: box.y, w: box.w, h: box.h },
    };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag || !surfaceRef.current) return;
      const p = pointToNorm(e);

      setBoxes((prev) => prev.map((b) => {
        if (b.localId !== drag.localId) return b;

        if (drag.mode === 'draw') {
          return {
            ...b,
            x: Math.min(drag.start.x, p.x),
            y: Math.min(drag.start.y, p.y),
            w: Math.abs(p.x - drag.start.x),
            h: Math.abs(p.y - drag.start.y),
          };
        }

        if (drag.mode === 'move') {
          const dx = p.x - drag.start.x;
          const dy = p.y - drag.start.y;
          return {
            ...b,
            x: Math.min(1 - drag.origin.w, Math.max(0, drag.origin.x + dx)),
            y: Math.min(1 - drag.origin.h, Math.max(0, drag.origin.y + dy)),
          };
        }

        // resize: the dragged corner follows the pointer, the opposite one
        // stays put, and the box is re-normalised so dragging past the far
        // edge flips it rather than producing a negative width.
        const o = drag.origin;
        let x1 = o.x;
        let y1 = o.y;
        let x2 = o.x + o.w;
        let y2 = o.y + o.h;
        if (drag.handle.includes('w')) x1 = p.x;
        if (drag.handle.includes('e')) x2 = p.x;
        if (drag.handle.includes('n')) y1 = p.y;
        if (drag.handle.includes('s')) y2 = p.y;
        return {
          ...b,
          x: Math.min(x1, x2),
          y: Math.min(y1, y2),
          w: Math.abs(x2 - x1),
          h: Math.abs(y2 - y1),
        };
      }));
    };

    const onUp = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      setDirty(true);
      // Drop anything too small to have been meant.
      setBoxes((prev) => prev.filter((b) => b.w >= MIN_SIZE && b.h >= MIN_SIZE));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // --- keyboard ---

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.matches('input, textarea, select')) return;

      if (e.key === '1' || e.key === '2') {
        const label = LABELS[Number(e.key) - 1];
        setActiveLabel(label);
        if (selectedId) updateBox(selectedId, { label });
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        removeBox(selectedId);
        return;
      }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
      if (e.key === 's' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); save('done'); return; }
      if (e.key === 'Enter') { e.preventDefault(); saveAndNext(); }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // updateBox/removeBox close over setBoxes only, which is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, go, save, saveAndNext, removeBox]);

  const progress = useMemo(() => {
    const done = frames.filter((f) => (f.doneBy || []).includes(annotator)).length;
    // Skipped frames are handled, so they count towards "am I finished" even
    // though they never reach the scoring.
    const skipped = frames.filter((f) => (f.skippedBy || []).includes(annotator)).length;
    return { done, skipped, total: frames.length };
  }, [frames, annotator]);

  const selected = boxes.find((b) => b.localId === selectedId);

  if (loading) return <Layout><div className="text-muted">読み込み中…</div></Layout>;

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">{dataset?.name} <span className="text-muted">— PH0 アノテーション</span></h5>
          <small className="text-muted">
            フレーム {frames.length ? index + 1 : 0} / {frames.length} ・ {annotator} の完了 {progress.done} / {progress.total}
            {progress.skipped > 0 && ` ・ 除外 ${progress.skipped}`}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label className="small text-muted mb-0">アノテータ</label>
          <input
            className="form-control form-control-sm"
            style={{ width: 90 }}
            value={annotator}
            onChange={(e) => setAnnotator(e.target.value.trim() || 'A')}
          />
          <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${datasetId}/report`}>レポート</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/poc">一覧</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {frames.length === 0 && (
        <div className="alert alert-light border">
          このデータセットにはまだフレームがありません。
          <code className="ms-1">go run ./cmd/poc-extract-frames -dataset {datasetId} -video &lt;動画&gt; -n 30</code>
        </div>
      )}

      {frame && (
        <div className="row g-3">
          <div className="col-lg-9">
            {/* The drawing surface has to be the photograph, nothing more.
                It used to be a full-width box with the portrait frame letterboxed
                inside it, so the black margins counted as drawable area and every
                box was stored squeezed into the middle 38% of the x axis. The
                error was invisible on screen — boxes were drawn back through the
                same wrong mapping — and only showed up against the detector.
                `width: fit-content` makes the box hug the image. */}
            <div className="text-center bg-dark rounded" style={{ overflow: 'hidden' }}>
              <div
                ref={surfaceRef}
                onMouseDown={onSurfaceMouseDown}
                className="position-relative d-inline-block"
                style={{ userSelect: 'none', cursor: 'crosshair', lineHeight: 0 }}
              >
                <img
                  src={frameImageURL(frame.imageUrl)}
                  alt={`frame ${frame.seq}`}
                  draggable={false}
                  style={{ display: 'block', maxHeight: '72vh', maxWidth: '100%', width: 'auto' }}
                />

              {showDetector && detectorBoxes.map((b) => (
                <div
                  key={`det-${b.id}`}
                  style={{
                    position: 'absolute',
                    left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                    width: `${b.w * 100}%`, height: `${b.h * 100}%`,
                    border: '2px dotted #f97316',
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {showOther && submitted && otherBoxes.map((b) => (
                <div
                  key={b.id}
                  className="position-absolute"
                  style={{
                    left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                    width: `${b.w * 100}%`, height: `${b.h * 100}%`,
                    border: `2px dashed ${LABEL_STYLE[b.label]?.color || '#fff'}`,
                    opacity: 0.65, pointerEvents: 'none',
                  }}
                />
              ))}

              {boxes.map((b) => {
                const color = LABEL_STYLE[b.label]?.color || '#fff';
                const isSelected = b.localId === selectedId;
                return (
                  <div
                    key={b.localId}
                    className="position-absolute"
                    style={{
                      left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                      width: `${b.w * 100}%`, height: `${b.h * 100}%`,
                      border: `2px solid ${color}`,
                      background: isSelected ? `${color}22` : 'transparent',
                      boxShadow: isSelected ? '0 0 0 1px #fff' : 'none',
                      // The interior does not take the mouse. A cabinet box
                      // covers most of the frame and the panels are inside it,
                      // so an interior that swallowed drags would make the one
                      // annotation this PoC is about impossible to draw. Grab
                      // the edge or the tag to move it instead.
                      pointerEvents: 'none',
                    }}
                  >
                    <span
                      onMouseDown={(e) => onBoxMouseDown(e, b, null)}
                      className="position-absolute badge"
                      style={{
                        top: -18, left: -2, background: color, fontSize: '0.625rem',
                        pointerEvents: 'auto', cursor: 'move',
                      }}
                    >
                      {LABEL_STYLE[b.label]?.short || '?'}{b.occluded ? ' ◐' : ''}{b.truncated ? ' ⇥' : ''}
                    </span>

                    {EDGES.map((edge) => (
                      <span
                        key={edge.key}
                        onMouseDown={(e) => onBoxMouseDown(e, b, null)}
                        style={{ position: 'absolute', pointerEvents: 'auto', cursor: 'move', ...edge.style }}
                      />
                    ))}

                    {isSelected && ['nw', 'ne', 'sw', 'se'].map((h) => (
                      <span
                        key={h}
                        onMouseDown={(e) => onBoxMouseDown(e, b, h)}
                        style={{
                          position: 'absolute',
                          width: 10, height: 10, background: '#fff', border: `1px solid ${color}`,
                          left: h.includes('w') ? -5 : undefined,
                          right: h.includes('e') ? -5 : undefined,
                          top: h.includes('n') ? -5 : undefined,
                          bottom: h.includes('s') ? -5 : undefined,
                          cursor: `${h}-resize`,
                          pointerEvents: 'auto',
                        }}
                      />
                    ))}
                  </div>
                );
              })}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" onClick={() => go(-1)} disabled={index === 0}>← 前</button>
                <button className="btn btn-outline-secondary" onClick={() => go(1)} disabled={index >= frames.length - 1}>次 →</button>
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                ドラッグで描画 ・ 枠の移動は枠線かタグを掴む ・ <kbd>1</kbd> Panel ・ <kbd>2</kbd> Cabinet ・ <kbd>Del</kbd> 削除 ・ <kbd>S</kbd> 保存 ・ <kbd>Enter</kbd> 保存して次へ
              </div>
              <div className="d-flex gap-2">
                {skipReason ? (
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => save('in_progress')}
                    disabled={saving}
                    title="除外を取り消して、この画像を通常の作業に戻します"
                  >
                    除外を取り消す
                  </button>
                ) : (
                  // A native select rather than a Bootstrap dropdown: the
                  // action bar sits inside a scrolling column, and a menu
                  // rendered in the document flow gets clipped by it. The
                  // browser draws this one above everything.
                  <select
                    className="form-select form-select-sm border-danger text-danger"
                    style={{ width: 'auto' }}
                    value=""
                    disabled={saving}
                    title="判断できない画像を採点対象から外します"
                    onChange={(e) => { if (e.target.value) save('skipped', e.target.value); }}
                  >
                    <option value="">使わない…</option>
                    {skipReasons.map((key) => (
                      <option key={key} value={key}>{SKIP_REASON_LABELS[key] || key}</option>
                    ))}
                  </select>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => save('in_progress')} disabled={saving}>
                  途中保存
                </button>
                <button className="btn btn-sm btn-primary" onClick={saveAndNext} disabled={saving || !!skipReason}>
                  {saving ? '保存中…' : '完了にして次へ'}
                </button>
              </div>
            </div>
          </div>

          <div className="col-lg-3">
            <div className="card mb-3">
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="card-title mb-0">この画像</h6>
                  {skipReason ? (
                    <span className="badge bg-danger-subtle text-danger-emphasis">
                      除外・{SKIP_REASON_LABELS[skipReason] || skipReason}
                    </span>
                  ) : submitted ? (
                    <span className="badge bg-success-subtle text-success-emphasis">完了</span>
                  ) : dirty ? (
                    <span className="badge bg-warning-subtle text-warning-emphasis">未保存</span>
                  ) : (
                    <span className="badge bg-secondary-subtle text-secondary-emphasis">未提出</span>
                  )}
                </div>
                <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  t = {Number(frame.timestampSec).toFixed(1)}s ・ bbox {boxes.length} 件
                </div>

                <div className="btn-group btn-group-sm w-100 mb-3">
                  {LABELS.map((label) => (
                    <button
                      key={label}
                      className={`btn ${activeLabel === label ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={() => {
                        setActiveLabel(label);
                        if (selectedId) updateBox(selectedId, { label });
                      }}
                    >
                      <span style={{ color: LABEL_STYLE[label].color }}>■</span> {LABEL_STYLE[label].label}
                    </button>
                  ))}
                </div>

                <div className="mb-1 small text-muted">撮影条件</div>
                <div className="d-flex flex-wrap gap-1">
                  {Object.entries(CONDITION_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      className={`btn btn-sm ${conditions.includes(key) ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}
                      onClick={() => toggleCondition(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {selected && (
              <div className="card mb-3">
                <div className="card-body py-3">
                  <h6 className="card-title">選択中の bbox</h6>
                  {/* 遮蔽と見切れは別の軸。遮蔽は現実のシーンの問題（人・ポスター・
                      別の筐体が前にある）、見切れは画角の問題（実体がフレームの外に
                      続いている）。まとめると、2in1 の片側だけが写ったフレームを
                      1ブロックとして数えてしまい、Recall が 100% に見える。 */}
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="occluded"
                      checked={!!selected.occluded}
                      onChange={(e) => updateBox(selected.localId, { occluded: e.target.checked })}
                    />
                    <label className="form-check-label small" htmlFor="occluded">
                      遮蔽 <span className="text-muted">— 人・ポスター・別の台で隠れている</span>
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="truncated"
                      checked={!!selected.truncated}
                      onChange={(e) => updateBox(selected.localId, { truncated: e.target.checked })}
                    />
                    <label className="form-check-label small" htmlFor="truncated">
                      見切れ <span className="text-muted">— 画面の外に続いている</span>
                    </label>
                  </div>
                  {/* 機構は block の属性であって、block である条件ではない。
                      実店舗の30枚中12枚が爪を持たない機械だった（タイミング式・
                      ルーレット式）。爪を条件にすると「この店には台が無い」に
                      なってしまうが、客から見ればどれも遊べるプライズ機。 */}
                  {selected.label === 'block' && (
                    <div className="mb-2">
                      <label className="form-label small mb-1">
                        景品の取り方 <span className="text-muted">— 分からなければ空のまま</span>
                      </label>
                      <select
                        className="form-select form-select-sm mb-1"
                        value={selected.mechanismType || ''}
                        onChange={(e) => updateBox(selected.localId, { mechanismType: e.target.value })}
                      >
                        <option value="">未選択</option>
                        {(vocabulary.mechanisms || []).map((m) => (
                          <option key={m} value={m}>{MECHANISM_LABELS[m] || m}</option>
                        ))}
                      </select>
                      {selected.mechanismType === 'claw' && (
                        <input
                          type="number"
                          min={1}
                          max={4}
                          className="form-control form-control-sm"
                          placeholder="爪の本数（分かれば）"
                          value={selected.clawCount ?? ''}
                          onChange={(e) => updateBox(selected.localId, { clawCount: e.target.value })}
                        />
                      )}
                    </div>
                  )}
                  <label className="form-label small mb-1">物理 PlayPosition</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={selected.playPositionId || ''}
                    onChange={(e) => updateBox(selected.localId, { playPositionId: e.target.value })}
                  >
                    <option value="">未割当</option>
                    {playPositions.map((p) => (
                      <option key={p.id} value={p.id}>{p.code}</option>
                    ))}
                  </select>
                  <button className="btn btn-sm btn-outline-danger w-100" onClick={() => removeBox(selected.localId)}>
                    削除
                  </button>
                </div>
              </div>
            )}

            <div className="card mb-3">
              <div className="card-body py-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showOther"
                    checked={showOther}
                    disabled={!submitted}
                    onChange={(e) => setShowOther(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="showOther">
                    他アノテータの枠を重ねる
                  </label>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  {submitted
                    ? `他 ${otherBoxes.length} 件`
                    : '独立性を保つため、自分が提出するまで表示できません。'}
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showDetector"
                    checked={showDetector}
                    disabled={detectorBoxes.length === 0}
                    onChange={(e) => setShowDetector(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="showDetector">
                    検出器の枠を重ねる（<span style={{ color: '#f97316' }}>橙の点線</span>）
                  </label>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  {detectorBoxes.length
                    ? `検出器 ${detectorBoxes.length} 件`
                    : '検出結果はまだ投入されていません。'}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body py-2" style={{ maxHeight: '32vh', overflowY: 'auto' }}>
                <div className="small text-muted mb-2">フレーム一覧</div>
                <div className="d-flex flex-wrap gap-1">
                  {frames.map((f, i) => {
                    const done = (f.doneBy || []).includes(annotator);
                    // Three states, three colours. A skipped frame used to look
                    // exactly like one nobody had opened, which is the one
                    // confusion that makes an annotator redo finished work.
                    const skipped = (f.skippedBy || []).includes(annotator);
                    const variant = i === index
                      ? 'btn-primary'
                      : skipped ? 'btn-outline-danger'
                        : done ? 'btn-success' : 'btn-outline-secondary';
                    return (
                      <button
                        key={f.id}
                        onClick={() => setIndex(i)}
                        className={`btn btn-sm ${variant}`}
                        style={{ width: 34, fontSize: '0.7rem', padding: '0.15rem 0' }}
                        title={`t=${Number(f.timestampSec).toFixed(1)}s${skipped ? ' ・ 除外' : ''}`}
                      >
                        {skipped ? '—' : f.seq}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
